import { Upload, Button, Image, message } from "antd";
import { MyColor } from "../config/myStyle";
import ImgCrop from 'antd-img-crop';
import { UploadFile, UploadProps } from "antd/es/upload";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const uploadBtnStyle: React.CSSProperties = {
    width: '100%',
    color: "white",
    border: "none",
    borderRadius: 38/2,
    fontWeight: 500,
    fontSize: 20,
    height: 38,
    margin: "12px 0",
    cursor: "pointer",
    background: MyColor.sub,
};

const infoStyle: React.CSSProperties = {
    color: "#888",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
};

type MyUploadProps = {
    value?: any[]
    onChange?: (value?: any[]) => void;
    uploadProps?: UploadProps
    maxCount?: number
    aspect?: number
    crop?: boolean
    tipHtml?: string
    buttonText?: string
    maxSizeMB?: number
    accept?: string
}

export const MyUpload = (props: MyUploadProps) => {
    const { t } = useTranslation();
    const [previewOpen, setPreviewOpen] = useState(false);

    const handlePreview = async (file: UploadFile) => {
        setPreviewOpen(true);
    };

    // 先檢查原圖，不能以裁切後的像素或檔案大小掩蓋原圖品質。
    const warnResolution = (file: File) => {
        if (!file.type?.startsWith('image/')) return;
        const url = URL.createObjectURL(file);
        const image = new window.Image();
        image.onload = () => {
            if (image.naturalWidth < 640 || image.naturalHeight < 360) message.warning(t('my_upload.low_resolution'));
            URL.revokeObjectURL(url);
        };
        image.onerror = () => URL.revokeObjectURL(url);
        image.src = url;
    };

    const uploadNode = (
        <Upload.Dragger
            listType='picture'
            method={'POST'}
            accept={props.accept ?? "image/jpeg,image/png,image/gif,image/webp"}
            onRemove={(file) => {
                const files = (props.value || []).filter(value => value.uid !== file.uid)
                props.onChange && props.onChange(files.length > 0 ? files : undefined)
            }}
            onPreview={handlePreview}
            beforeUpload={(file: any, files) => {
                const maxSizeMB = props.maxSizeMB ?? 5;
                const fileSizeMB = Number(file.size || 0) / 1024 / 1024;
                if (fileSizeMB >= maxSizeMB) {
                    message.error(t('my_upload.file_too_large', { size: maxSizeMB }));
                    return Upload.LIST_IGNORE;
                }

                if (props.crop === false) warnResolution(file);
                let newFiles = [...(props.value || [])]
                if (props.maxCount && props.maxCount <= newFiles.length) {
                    newFiles.shift()
                }
                Promise.all(
                    [...newFiles, ...files]
                        .map(async (file: any) => {
                            if (!file.thumbUrl) {
                                const r = new FileReader();
                                r.readAsDataURL(file);
                                return await new Promise((resolve) => {
                                    r.onload = e => {
                                        file.thumbUrl = e.target?.result
                                        file.url = e.target?.result
                                        file.status = 'done'
                                        resolve(file)
                                    };
                                })
                            } else {
                                return file
                            }
                        })
                )
                    .then(newFiles => {
                        props.onChange && props.onChange(newFiles)
                    })
                return false
            }}
            showUploadList={true}
            style={{ borderRadius: 10, background: "white", borderStyle: 'solid', borderWidth: 1 }}
            fileList={props.value}
            {...props.uploadProps}
        >
            <div className="px-[5px]">
                <Button style={uploadBtnStyle}>{props.buttonText ?? t('my_upload.upload_image')}</Button>
                <div style={infoStyle} dangerouslySetInnerHTML={{ __html: props.tipHtml ?? t('my_upload.upload_tips') }} />
            </div>
        </Upload.Dragger>
    );

    return (
        <>
            {props.crop === false ? uploadNode : (
                <ImgCrop beforeCrop={(file: File) => {
                    const max = props.maxSizeMB ?? 5;
                    if (file.size >= max * 1024 * 1024) {
                        message.error(t('my_upload.file_too_large', { size: max }));
                        return false;
                    }
                    warnResolution(file);
                    return true;
                }} rotationSlider
                    aspect={props.aspect ?? 16 / 9}
                    quality={1}
                >
                    {uploadNode}
                </ImgCrop>
            )}
            <div>
                {props.value?.map(item => (
                    <Image
                        key={item.uid || item.url}
                        style={{ height: 100, width: 100, borderColor: 'red' }}
                        styles={{ root: { display: 'none' } }}
                        preview={{
                            open: previewOpen,
                            onOpenChange: (visible) => setPreviewOpen(visible),
                        }}
                        src={item.url}
                    />
                ))}
            </div>
        </>
    )
}
